/*
 * Resolves an uploaded image (profile pictures, mainly) to something an <img>
 * can show now that the bucket is private: the bytes are fetched through the
 * authenticated file proxy and handed back as an object URL. One request per
 * distinct storage URL per page load - avatars repeat a lot in tables - and a
 * failed or unauthenticated lookup just yields undefined so callers fall back
 * to initials. Non-uploaded URLs (static assets, external images) pass through.
 */
import { useEffect, useState } from "react";

import api from "../Network/ApiService";
import { getFileExtension, getSecureFileUrl } from "../Library/Utils/fileAccess";
import { isAuth } from "../Library/Utils/auth";

const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};

const pending = new Map<string, Promise<string>>();
const resolved = new Map<string, string>();

const loadSecureImage = (url: string): Promise<string> => {
  const inFlight = pending.get(url);
  if (inFlight) return inFlight;

  const request = api
    .get(getSecureFileUrl(url), { responseType: "blob" })
    .then((response) => {
      const mime = IMAGE_MIME_BY_EXTENSION[getFileExtension(url)];
      const blob: Blob = mime ? new Blob([response.data], { type: mime }) : response.data;
      const objectUrl = window.URL.createObjectURL(blob);
      resolved.set(url, objectUrl);
      return objectUrl;
    })
    .finally(() => pending.delete(url));

  pending.set(url, request);
  return request;
};

const useSecureImage = (url?: string | null): string | undefined => {
  const needsProxy = !!url && getSecureFileUrl(url) !== url;
  const [src, setSrc] = useState<string | undefined>(() => {
    if (!url) return undefined;
    return needsProxy ? resolved.get(url) : url;
  });

  useEffect(() => {
    if (!url) {
      setSrc(undefined);
      return undefined;
    }
    if (!needsProxy) {
      setSrc(url);
      return undefined;
    }
    const cached = resolved.get(url);
    if (cached) {
      setSrc(cached);
      return undefined;
    }
    // Pre-login pages (invite acceptance) show the inviter's avatar; a 401
    // from the proxy there would bounce the visitor to sign-in.
    if (!isAuth()) {
      setSrc(undefined);
      return undefined;
    }

    let cancelled = false;
    setSrc(undefined);
    loadSecureImage(url)
      .then((objectUrl) => {
        if (!cancelled) setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return src;
};

export default useSecureImage;
